import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { FileText, ArrowLeft } from "lucide-react";
import { inr, StatusBadge } from "@/components/billing/BillingParts";

export default function ClientBills() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/billing/staff/${id}/invoices`).then(r => setData(r.data)).catch(e => setErr(e.response?.data?.detail || e.message)); }, [id]);
  if (err) return <div className="text-sm text-rose-600">{err}</div>;
  if (!data) return <div className="text-sm text-zinc-500">Loading…</div>;
  const { profile, invoices } = data;
  const billed = invoices.filter(i => i.status !== "cancelled").reduce((s, i) => s + i.total, 0);
  const gst = invoices.filter(i => i.status !== "cancelled" && i.with_gst).reduce((s, i) => s + (i.tax_total || 0), 0);
  return (
    <div className="space-y-6" data-testid="client-bills-page">
      <header className="flex items-end justify-between gap-3">
        <div>
          <Link to="/clients" data-testid="bills-back" className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-500 hover:text-zinc-900 flex items-center gap-1"><ArrowLeft size={12} /> Clients</Link>
          <h1 className="font-heading font-black tracking-tight text-3xl sm:text-4xl mt-2">{profile.business_name || "Client"} · Sell bills</h1>
          <p className="text-sm text-zinc-600 mt-1">Read-only view of bills the client raised to their own customers{profile.gstin ? ` · GSTIN ${profile.gstin}` : ""}</p>
        </div>
        <div className="text-right text-xs text-zinc-600">
          <div>Billed <b className="tabular">{inr(billed)}</b></div>
          <div>GST collected <b className="tabular">{inr(gst)}</b></div>
        </div>
      </header>
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50"><tr className="text-[10px] tracking-[0.14em] uppercase text-zinc-500">{["Bill no.", "Date", "Customer", "Type", "Taxable", "Tax", "Total", "Paid", "Status", ""].map(h => <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {invoices.map(i => (
              <tr key={i.id} data-testid={`staff-bill-${i.id}`} className="border-t border-zinc-100">
                <td className="px-3 py-2 font-medium tabular">{i.invoice_number}</td><td className="px-3 py-2 tabular">{i.issue_date}</td><td className="px-3 py-2">{i.customer_snapshot.name}</td>
                <td className="px-3 py-2 text-xs">{i.with_gst ? (i.intra_state ? "GST (CGST+SGST)" : "GST (IGST)") : "No GST"}</td>
                <td className="px-3 py-2 tabular">{inr(i.subtotal)}</td><td className="px-3 py-2 tabular">{inr(i.tax_total)}</td><td className="px-3 py-2 tabular font-medium">{inr(i.total)}</td><td className="px-3 py-2 tabular">{inr(i.paid_amount)}</td>
                <td className="px-3 py-2"><StatusBadge s={i.status} /></td>
                <td className="px-3 py-2"><a data-testid={`staff-bill-pdf-${i.id}`} href={`${API_BASE}/billing/invoices/${i.id}/pdf`} target="_blank" rel="noreferrer" className="p-1 inline-block hover:bg-zinc-100"><FileText size={14} /></a></td>
              </tr>
            ))}
            {!invoices.length && <tr><td colSpan={10} className="px-3 py-10 text-center text-zinc-500 text-sm">This client hasn't created any sell bills yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
